import { SETTINGS_CATEGORIES } from "./nav.js";

/** One searchable setting. `id` matches a SettingsCard/SettingsAnchor id on the
 *  page so a result can scroll + flash it. `route` is the category segment. */
export interface SettingEntry {
  id: string;
  title: string;
  /** Extra terms to match against (synonyms, related words). */
  keywords: string;
  route: string;
  /** `true` = only present when developer mode is on. */
  devOnly?: boolean;
  /** `true` = desktop only (hidden from mobile). */
  desktopOnly?: boolean;
}

/** Every setting card, grouped by category route. Keep ids in sync with pages. */
const ENTRIES: SettingEntry[] = [
  // General
  { id: "language", title: "Language", keywords: "locale english german deutsch translation", route: "general" },
  { id: "time-format", title: "Time format", keywords: "12 24 hour clock am pm", route: "general" },
  { id: "week-start", title: "Week starts on", keywords: "monday sunday first day", route: "general" },
  { id: "multi-day-focus", title: "Week & X-Days focus", keywords: "current week today yesterday leftmost rolling calendar", route: "general" },
  { id: "default-tz", title: "Default time zone", keywords: "timezone tz utc floating region", route: "general" },
  { id: "default-view", title: "Default view", keywords: "startup launch home today month week", route: "general" },
  { id: "reset-prefs", title: "Reset all preferences", keywords: "defaults restore clear", route: "general" },
  { id: "show-onboarding", title: "Setup guide", keywords: "onboarding first run welcome tour setup wizard", route: "general" },
  // Appearance
  { id: "theme", title: "Theme", keywords: "dark light system appearance colour color mode", route: "appearance" },
  { id: "block-color", title: "Block color", keywords: "tint gradient task event wash calendar", route: "appearance" },
  { id: "text-size", title: "Text size", keywords: "font scale typography bigger smaller accessibility", route: "appearance" },
  { id: "text-font", title: "Text font", keywords: "typeface typography system serif rounded mono", route: "appearance" },
  // Tasks & calendar
  { id: "default-calendar", title: "Default calendar", keywords: "new task event calendar", route: "tasks" },
  { id: "default-type", title: "Default entry type", keywords: "event task new create", route: "tasks" },
  { id: "default-duration", title: "Default event length", keywords: "duration minutes time block", route: "tasks" },
  { id: "default-task-reminders", title: "Default task reminders", keywords: "notification alert lead time before due", route: "tasks" },
  { id: "default-event-reminders", title: "Default event reminders", keywords: "notification alert lead time before due", route: "tasks" },
  { id: "snooze", title: "Snooze anchors", keywords: "morning evening hour presets defer", route: "tasks" },
  { id: "agenda-days", title: "Today agenda", keywords: "today horizon days 3 7 14 empty schedule layout", route: "tasks" },
  { id: "subtask-rollup", title: "Subtask completion", keywords: "rollup parent child two-way", route: "tasks" },
  { id: "matrix-scope", title: "Priority matrix", keywords: "eisenhower importance urgency 3x3 grid scope tasks events off flags", route: "tasks" },
  { id: "priority-input", title: "Priority input", keywords: "matrix flags rail control form editor presentation", route: "tasks" },
  { id: "deadline-urgency", title: "Deadline-based urgency", keywords: "auto derive matrix days threshold high medium due overdue", route: "tasks" },
  { id: "topic-editor", title: "Create/edit topics", keywords: "topic buttons icons text multiple single", route: "tasks" },
  { id: "swipe-hint", title: "Calendar swipe hint", keywords: "gesture mobile filters inbox", route: "tasks" },
  // Navigation
  { id: "sidebar-visibility", title: "Show in sidebar", keywords: "nav hide today calendar inbox search claude filters", route: "navigation" },
  { id: "keyboard-shortcuts", title: "Keyboard shortcuts", keywords: "rebind keys hotkey chord", route: "navigation", desktopOnly: true },
  // Organize
  { id: "topics", title: "Topics", keywords: "categories nested tags icon color reorder show in month today hide exclusions", route: "organize" },
  { id: "calendars", title: "Calendars", keywords: "create edit color visibility default reorder show in month today hide exclusions", route: "organize" },
  { id: "labels", title: "Labels", keywords: "tags color reorder", route: "organize" },
  { id: "organize-board", title: "Palette board", keywords: "color paint swatch topic calendar label theme", route: "organize" },
  // Sync & accounts
  { id: "account", title: "Account", keywords: "sign in up login logout email password delete", route: "sync" },
  { id: "sync", title: "Multi-device sync", keywords: "devices server merge", route: "sync" },
  { id: "google-calendar", title: "Google Calendar", keywords: "connect oauth two-way", route: "sync" },
  // Data & privacy
  { id: "anthropic-key", title: "Anthropic API key", keywords: "claude ai token secret", route: "data" },
  { id: "ics", title: "Import / export calendar (.ics)", keywords: "ical icalendar export import", route: "data" },
  { id: "settings-file", title: "Export / import settings", keywords: "preferences config transfer", route: "data" },
  { id: "backup", title: "Backup & restore", keywords: "save load file delete all wipe export import data", route: "data" },
  // Trash
  { id: "trash", title: "Trash", keywords: "deleted restore purge empty bin", route: "trash" },
  // About & feedback
  { id: "about", title: "About", keywords: "version build links website", route: "about" },
  { id: "feedback", title: "Feedback", keywords: "email send note bug report", route: "about" },
  { id: "screenshot-feedback", title: "Screenshot feedback", keywords: "mobile screenshot composer", route: "about" },
  { id: "developer-options", title: "Developer options", keywords: "debug advanced unlock", route: "about" },
  // Developer (devOnly)
  { id: "app-info", title: "App info", keywords: "version platform storage adapter build", route: "developer", devOnly: true },
  { id: "database-info", title: "Database", keywords: "storage sqlite postgres online remote server size rows tables schema columns tombstones state makeup", route: "developer", devOnly: true },
  { id: "error-log", title: "Error log", keywords: "errors crash debug runtime", route: "developer", devOnly: true },
  { id: "debug-flags", title: "Debug flags", keywords: "notify gsync ink mobile ux logging", route: "developer", devOnly: true },
  { id: "touch-pan", title: "Touch pan", keywords: "swipe amplification fling debug calendar", route: "developer", devOnly: true },
  { id: "notification-test", title: "Notification test", keywords: "diagnostics reminders firing test", route: "developer", devOnly: true },
];

export interface SettingResult extends SettingEntry {
  categoryLabel: string;
}

const labelByRoute = new Map(SETTINGS_CATEGORIES.map((c) => [c.route, c.label]));

/** Case-insensitive substring match over title + keywords, scoped by the
 *  current developer-mode / mobile state. */
export function searchSettings(query: string, opts: { developerMode: boolean; mobile: boolean }): SettingResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return ENTRIES.filter((e) => {
    if (e.devOnly && !opts.developerMode) return false;
    if (e.desktopOnly && opts.mobile) return false;
    return `${e.title} ${e.keywords}`.toLowerCase().includes(q);
  }).map((e) => ({ ...e, categoryLabel: labelByRoute.get(e.route) ?? e.route }));
}
